/**
 * The small amount of linear algebra the gaze pipeline needs, and nothing more.
 *
 * This exists because the browser has no OpenCV. The Python reference
 * (eye-tracking-sandbox) leans on cv2 for solvePnP, Rodrigues and matrix
 * inversion; porting the pipeline faithfully means reimplementing exactly those
 * pieces, so they live here rather than being scattered through the geometry.
 *
 * Matrices are row-major flat arrays — Mat3 is 9 numbers, not an array of rows.
 * Nested arrays would read closer to the numpy source, but this code runs once
 * per camera frame inside a requestAnimationFrame callback and flat arrays keep
 * it allocation-light. Every function is pure and allocates its own result.
 */

export type Vec3 = [number, number, number]
/** Row-major 3×3: indices 0,1,2 are the first row. */
export type Mat3 = number[]

export function matMul3(a: Mat3, b: Mat3): Mat3 {
  const out = new Array<number>(9)
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]
    }
  }
  return out
}

/** M · v for a column vector v. */
export function matVec3(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ]
}

export function transpose3(m: Mat3): Mat3 {
  return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]
}

export function identity3(): Mat3 {
  return [1, 0, 0, 0, 1, 0, 0, 0, 1]
}

/** Column `c` (0-based) of a row-major matrix. */
export function column3(m: Mat3, c: number): Vec3 {
  return [m[c], m[3 + c], m[6 + c]]
}

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

export function norm3(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2])
}

/** Unit vector, or null when the input is degenerate enough that direction is meaningless. */
export function normalize3(v: Vec3): Vec3 | null {
  const n = norm3(v)
  if (!Number.isFinite(n) || n < 1e-12) return null
  return [v[0] / n, v[1] / n, v[2] / n]
}

export function sub3(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

export function add3(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

export function scale3(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s]
}

export function det3(m: Mat3): number {
  return (
    m[0] * (m[4] * m[8] - m[5] * m[7]) -
    m[1] * (m[3] * m[8] - m[5] * m[6]) +
    m[2] * (m[3] * m[7] - m[4] * m[6])
  )
}

/** Inverse by cofactors, or null when singular. */
export function inverse3(m: Mat3): Mat3 | null {
  const d = det3(m)
  if (!Number.isFinite(d) || Math.abs(d) < 1e-14) return null
  const i = 1 / d
  return [
    (m[4] * m[8] - m[5] * m[7]) * i,
    (m[2] * m[7] - m[1] * m[8]) * i,
    (m[1] * m[5] - m[2] * m[4]) * i,
    (m[5] * m[6] - m[3] * m[8]) * i,
    (m[0] * m[8] - m[2] * m[6]) * i,
    (m[2] * m[3] - m[0] * m[5]) * i,
    (m[3] * m[7] - m[4] * m[6]) * i,
    (m[1] * m[6] - m[0] * m[7]) * i,
    (m[0] * m[4] - m[1] * m[3]) * i,
  ]
}

/**
 * Rodrigues: rotation vector → rotation matrix. Mirrors cv2.Rodrigues so a
 * golden-vector rvec from the Python reference can be fed in directly.
 */
export function rodrigues(rvec: Vec3): Mat3 {
  const theta = norm3(rvec)
  if (theta < 1e-12) return identity3()
  const [x, y, z] = [rvec[0] / theta, rvec[1] / theta, rvec[2] / theta]
  const c = Math.cos(theta)
  const s = Math.sin(theta)
  const t = 1 - c
  return [
    t * x * x + c,
    t * x * y - s * z,
    t * x * z + s * y,
    t * x * y + s * z,
    t * y * y + c,
    t * y * z - s * x,
    t * x * z - s * y,
    t * y * z + s * x,
    t * z * z + c,
  ]
}

/** Rotation matrix → rotation vector, the inverse of `rodrigues`. */
export function rotationToRvec(m: Mat3): Vec3 {
  // cos θ from the trace, clamped because accumulated error can push it past ±1.
  const cosTheta = Math.min(1, Math.max(-1, (m[0] + m[4] + m[8] - 1) / 2))
  const theta = Math.acos(cosTheta)
  if (theta < 1e-12) return [0, 0, 0]
  if (Math.PI - theta < 1e-6) {
    // Near π the antisymmetric part vanishes; recover the axis from the diagonal.
    const axis: Vec3 = [
      Math.sqrt(Math.max(0, (m[0] + 1) / 2)),
      Math.sqrt(Math.max(0, (m[4] + 1) / 2)),
      Math.sqrt(Math.max(0, (m[8] + 1) / 2)),
    ]
    // Signs are only determined up to a global flip; fix them off the largest component.
    if (axis[0] >= axis[1] && axis[0] >= axis[2]) {
      if (m[5] - m[7] < 0) axis[0] = -axis[0]
      axis[1] = Math.sign(m[1] + m[3]) * axis[1] * Math.sign(axis[0] || 1)
      axis[2] = Math.sign(m[2] + m[6]) * axis[2] * Math.sign(axis[0] || 1)
    }
    return scale3(axis, theta)
  }
  const k = theta / (2 * Math.sin(theta))
  return [(m[7] - m[5]) * k, (m[2] - m[6]) * k, (m[3] - m[1]) * k]
}

/** Unit quaternion (w, x, y, z) → rotation matrix. */
export function quaternionToMatrix(q: [number, number, number, number]): Mat3 {
  const [w, x, y, z] = q
  return [
    1 - 2 * (y * y + z * z),
    2 * (x * y - w * z),
    2 * (x * z + w * y),
    2 * (x * y + w * z),
    1 - 2 * (x * x + z * z),
    2 * (y * z - w * x),
    2 * (x * z - w * y),
    2 * (y * z + w * x),
    1 - 2 * (x * x + y * y),
  ]
}

/**
 * Eigenvector of the largest eigenvalue of a symmetric 4×4, by cyclic Jacobi.
 *
 * Only the dominant eigenvector is ever wanted (Horn's absolute-orientation
 * quaternion), but Jacobi produces the whole decomposition anyway and at 4×4 it
 * converges in a handful of sweeps — far less code than a dedicated power
 * iteration with the deflation needed to be safe against a bad start vector.
 */
export function largestEigenvector4(input: number[][]): [number, number, number, number] {
  const a = input.map((row) => [...row])
  const v = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1],
  ]

  for (let sweep = 0; sweep < 32; sweep++) {
    let off = 0
    for (let p = 0; p < 4; p++) {
      for (let q = p + 1; q < 4; q++) off += a[p][q] * a[p][q]
    }
    if (off < 1e-24) break

    for (let p = 0; p < 3; p++) {
      for (let q = p + 1; q < 4; q++) {
        if (Math.abs(a[p][q]) < 1e-18) continue
        const theta = (a[q][q] - a[p][p]) / (2 * a[p][q])
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1))
        const c = 1 / Math.sqrt(t * t + 1)
        const s = t * c

        for (let k = 0; k < 4; k++) {
          const akp = a[k][p]
          const akq = a[k][q]
          a[k][p] = c * akp - s * akq
          a[k][q] = s * akp + c * akq
        }
        for (let k = 0; k < 4; k++) {
          const apk = a[p][k]
          const aqk = a[q][k]
          a[p][k] = c * apk - s * aqk
          a[q][k] = s * apk + c * aqk
        }
        for (let k = 0; k < 4; k++) {
          const vkp = v[k][p]
          const vkq = v[k][q]
          v[k][p] = c * vkp - s * vkq
          v[k][q] = s * vkp + c * vkq
        }
      }
    }
  }

  let best = 0
  for (let i = 1; i < 4; i++) if (a[i][i] > a[best][best]) best = i
  const q: [number, number, number, number] = [v[0][best], v[1][best], v[2][best], v[3][best]]
  const n = Math.hypot(q[0], q[1], q[2], q[3])
  return n < 1e-12 ? [1, 0, 0, 0] : [q[0] / n, q[1] / n, q[2] / n, q[3] / n]
}

/**
 * Solve A·x = b by Gaussian elimination with partial pivoting. Returns null if
 * A is singular. Sized for the 6×6 normal equations of the pose refinement, so
 * it copies its inputs rather than working in place.
 */
export function solveLinearSystem(a: number[][], b: number[]): number[] | null {
  const n = b.length
  const m = a.map((row, i) => [...row, b[i]])
  for (let col = 0; col < n; col++) {
    let pivot = col
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r
    }
    if (Math.abs(m[pivot][col]) < 1e-14) return null
    if (pivot !== col) {
      const tmp = m[col]
      m[col] = m[pivot]
      m[pivot] = tmp
    }
    for (let r = col + 1; r < n; r++) {
      const factor = m[r][col] / m[col][col]
      if (factor === 0) continue
      for (let c = col; c <= n; c++) m[r][c] -= factor * m[col][c]
    }
  }
  const x = new Array<number>(n).fill(0)
  for (let r = n - 1; r >= 0; r--) {
    let acc = m[r][n]
    for (let c = r + 1; c < n; c++) acc -= m[r][c] * x[c]
    x[r] = acc / m[r][r]
  }
  return x.every(Number.isFinite) ? x : null
}

/**
 * Absolute orientation: the rotation R minimising Σ‖R·aᵢ − bᵢ‖² for two
 * centred point sets, via Horn's unit-quaternion method.
 *
 * Horn rather than an SVD Procrustes because the quaternion route cannot return
 * an improper rotation — an SVD solution needs an explicit det(R) = −1 fix-up,
 * and getting that wrong yields a mirrored head pose that still reprojects
 * plausibly on a near-symmetric face.
 */
export function absoluteOrientation(a: Vec3[], b: Vec3[]): Mat3 {
  const m = new Array<number>(9).fill(0)
  for (let i = 0; i < a.length; i++) {
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) m[r * 3 + c] += b[i][r] * a[i][c]
    }
  }
  const [sxx, sxy, sxz, syx, syy, syz, szx, szy, szz] = m

  const n: number[][] = [
    [sxx + syy + szz, syz - szy, szx - sxz, sxy - syx],
    [syz - szy, sxx - syy - szz, sxy + syx, szx + sxz],
    [szx - sxz, sxy + syx, -sxx + syy - szz, syz + szy],
    [sxy - syx, szx + sxz, syz + szy, -sxx - syy + szz],
  ]
  return quaternionToMatrix(largestEigenvector4(n))
}
